import React, { useEffect, useMemo, useState } from "react";
import { FileText, Upload, Download, Trash2, Loader2, CheckCircle2, XCircle, Lock } from "lucide-react";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../hooks/useAuth";
import {
  subscribeDeclarations,
  uploadDeclaration,
  deleteDeclaration,
} from "../../services/declarationsService";

const YEARS = [2026, 2025, 2024, 2023, 2022, 2021, 2020];

function formatUploadDate(ts) {
  if (!ts) return "—";
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// Timestamp Firestore (o Date/stringa) -> millisecondi, per ordinamento cronologico.
function toMillis(ts) {
  if (!ts) return 0;
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

// Decodifica una stringa base64 (senza prefisso data:) in un Blob e ne avvia il
// download, senza passare da un URL remoto: gli upload del commercialista sono
// salvati come base64 dentro Firestore (nessun fileUrl/Storage disponibile).
function downloadBase64File(base64, fileName) {
  try {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "dichiarazione.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Errore nella decodifica/download della dichiarazione:", err);
  }
}

// Sezione "Dichiarazioni dei Redditi" della pagina Commercialista: upload PDF
// da parte dell'utente + dichiarazioni caricate dal commercialista dal portale
// pubblico, mostrate insieme in un'unica lista ordinata per data di caricamento.
export default function DeclarationsSection({ darkMode }) {
  const { user } = useAuth();
  const uid = user?.uid;

  const [declarations, setDeclarations] = useState([]);
  const [accountantDeclarations, setAccountantDeclarations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [year, setYear] = useState(YEARS[0]);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Upload utente (Storage + Firestore).
  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsubscribe = subscribeDeclarations(uid, (items) => {
      setDeclarations(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [uid]);

  // Upload del commercialista (dal portale pubblico): salvati direttamente in
  // Firestore come base64, filtrati in memoria per type === "dichiarazione".
  useEffect(() => {
    if (!uid) {
      setAccountantDeclarations([]);
      return undefined;
    }
    const q = query(collection(db, "users", uid, "accountantUploads"), orderBy("uploadedAt", "desc"));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((item) => item.type === "dichiarazione");
        setAccountantDeclarations(items);
      },
      (err) => {
        console.error("Errore lettura dichiarazioni del commercialista:", err);
        setAccountantDeclarations([]);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  // Lista unificata (utente + commercialista), ordinata per data di caricamento decrescente.
  const mergedDeclarations = useMemo(() => {
    const userItems = declarations.map((item) => ({
      ...item,
      _kind: "user",
    }));
    const accountantItems = accountantDeclarations.map((item) => ({
      ...item,
      _kind: "accountant",
    }));
    return [...userItems, ...accountantItems].sort(
      (a, b) => toMillis(b.uploadedAt) - toMillis(a.uploadedAt)
    );
  }, [declarations, accountantDeclarations]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0] || null;
    setFormError(null);
    setFormSuccess(false);
    if (f && f.type !== "application/pdf") {
      setFormError("Seleziona un file PDF.");
      setFile(null);
      e.target.value = "";
      return;
    }
    setFile(f);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uid) return;
    if (!file) {
      setFormError("Seleziona un file PDF da caricare.");
      return;
    }
    setUploading(true);
    setFormError(null);
    setFormSuccess(false);
    try {
      await uploadDeclaration(uid, { year, file });
      setFile(null);
      const fileInput = document.getElementById("declaration-file-input");
      if (fileInput) fileInput.value = "";
      setFormSuccess(true);
      setTimeout(() => setFormSuccess(false), 3000);
    } catch (err) {
      console.error("Errore caricamento dichiarazione:", err);
      setFormError(err?.message || "Errore durante il caricamento. Riprova.");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!uid) return;
    setDeletingId(item.id);
    try {
      await deleteDeclaration(uid, item.id, item.storagePath);
      setConfirmDeleteId(null);
    } catch (err) {
      console.error("Errore eliminazione dichiarazione:", err);
      setFormError("Errore durante l'eliminazione. Riprova.");
    } finally {
      setDeletingId(null);
    }
  };

  const inputCls = darkMode
    ? "border-neutral-700 bg-neutral-800 text-neutral-200"
    : "border-neutral-200 bg-white text-neutral-800";

  return (
    <div>
      {/* Form upload */}
      <form onSubmit={handleUpload} className="flex flex-col sm:flex-row sm:items-end gap-3 mb-5">
        <div className="flex flex-col gap-1">
          <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>Anno</label>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className={`px-3 py-2 border rounded-lg text-sm ${inputCls}`}
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <label className={`text-xs ${darkMode ? "text-neutral-500" : "text-neutral-600"}`}>File PDF</label>
          <input
            id="declaration-file-input"
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className={`text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:cursor-pointer ${
              darkMode
                ? "text-neutral-400 file:bg-neutral-800 file:text-neutral-300"
                : "text-neutral-600 file:bg-neutral-100 file:text-neutral-700"
            }`}
          />
        </div>

        <button
          type="submit"
          disabled={uploading || !file}
          className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-forfy-500 text-white text-sm hover:bg-forfy-600 disabled:opacity-50 shrink-0"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Carica
        </button>
      </form>

      {formError && (
        <div className="mb-4 flex items-start gap-2 text-xs text-red-500">
          <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> {formError}
        </div>
      )}
      {formSuccess && (
        <div className="mb-4 flex items-start gap-2 text-xs text-green-600">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Dichiarazione caricata con successo.
        </div>
      )}

      {/* Lista */}
      {loading ? (
        <div className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>Caricamento...</div>
      ) : mergedDeclarations.length === 0 ? (
        <p className={`text-sm ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
          Nessuna dichiarazione caricata.
        </p>
      ) : (
        <ul className="space-y-2">
          {mergedDeclarations.map((item) => {
            const isUser = item._kind === "user";
            return (
              <li
                key={`${item._kind}-${item.id}`}
                className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                  darkMode ? "border-neutral-800 bg-neutral-950/40" : "border-neutral-100 bg-neutral-50"
                }`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className={`w-4 h-4 shrink-0 ${darkMode ? "text-indigo-400" : "text-indigo-600"}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-neutral-900"}`}>
                        {item.year ? `Dichiarazione ${item.year}` : "Dichiarazione"}
                      </span>
                      {isUser ? (
                        <span
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            darkMode ? "bg-neutral-700 text-neutral-300" : "bg-neutral-200 text-neutral-700"
                          }`}
                        >
                          Tu
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-500 text-white">
                          Commercialista
                        </span>
                      )}
                    </div>
                    <div className={`text-xs truncate ${darkMode ? "text-neutral-500" : "text-neutral-500"}`}>
                      {item.fileName} · caricata il {formatUploadDate(item.uploadedAt)}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {isUser ? (
                    <a
                      href={item.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Scarica dichiarazione ${item.year}`}
                      className={`p-2 rounded-lg ${
                        darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-500 hover:bg-neutral-100"
                      }`}
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  ) : (
                    <button
                      type="button"
                      onClick={() => downloadBase64File(item.fileDataBase64, item.fileName)}
                      aria-label={`Scarica dichiarazione caricata dal commercialista${item.fileName ? `: ${item.fileName}` : ""}`}
                      className={`p-2 rounded-lg ${
                        darkMode ? "text-neutral-400 hover:bg-neutral-800" : "text-neutral-500 hover:bg-neutral-100"
                      }`}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}

                  {!isUser ? (
                    <span
                      title="Caricato dal commercialista"
                      aria-label="Non eliminabile: caricato dal commercialista"
                      className={`p-2 rounded-lg opacity-40 cursor-not-allowed ${
                        darkMode ? "text-neutral-500" : "text-neutral-400"
                      }`}
                    >
                      <Lock className="w-4 h-4" />
                    </span>
                  ) : confirmDeleteId === item.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        disabled={deletingId === item.id}
                        className="px-2 py-1 rounded-lg bg-red-600 text-white text-xs hover:bg-red-700 disabled:opacity-50"
                      >
                        Conferma
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className={`px-2 py-1 rounded-lg text-xs ${
                          darkMode
                            ? "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
                            : "bg-neutral-200 text-neutral-700 hover:bg-neutral-300"
                        }`}
                      >
                        Annulla
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteId(item.id)}
                      aria-label={`Elimina dichiarazione ${item.year}`}
                      className={`p-2 rounded-lg ${
                        darkMode ? "text-red-400 hover:bg-red-900/20" : "text-red-500 hover:bg-red-50"
                      }`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
